class Location {
    constructor(x, y) {
        this.x = x;
        this.y = y;
    }
    getX() {
        return this.x;
    }
    getY() {
        return this.y;
    }
    setX(x) {
        this.x = x;
    }
    setY(y) {
        this.y = y;
    }
    getRelativeLocation(x, y) {
        return new Location(this.x + x, this.y + y);
    }
    equalsLocation(l2) {
        return this.getX() === l2.getX() && this.getY() === l2.getY();
    }
}

function canContainRectangle(containerWidth, containerLength, targetWidth, targetLength) {
    return ((containerLength >= targetLength && containerWidth >= targetWidth) ||
        (containerLength >= targetWidth && containerWidth >= targetWidth));
}
class Rectangle {
    constructor(width, length) {
        this.width = width;
        this.length = length;
    }
    canContainRectangle(containWidth, containLength) {
        return canContainRectangle(this.getWidth(), this.getLength(), containWidth, containLength);
    }
    getWidth() {
        return this.width;
    }
    getLength() {
        return this.length;
    }
    setWidth(width) {
        this.width = width;
    }
    setLength(length) {
        this.length = length;
    }
    equalsRectangle(rec2) {
        return this.getWidth() === rec2.getWidth() && rec2.getLength() === rec2.getLength();
    }
}

function canEnclose(subject, target) {
    return (subject.getXMin() <= target.getXMin() && subject.getYMin() <= target.getYMin() &&
        subject.getXMax() >= target.getXMax() && subject.getYMax() >= target.getYMax());
}
function grow(rectangleLocation, units) {
    const grown = rectangleLocation.clone();
    grown.rectangle.setLength(rectangleLocation.getRectangleLength() + 2 * units);
    grown.rectangle.setWidth(rectangleLocation.getRectangleWidth() + 2 * units);
    grown.location.setX(rectangleLocation.location.getX() - units);
    grown.location.setY(rectangleLocation.location.getY() - units);
    return grown;
}
function copyToLocation(rectangleLocation, x, y) {
    const clone = rectangleLocation.clone();
    clone.location = new Location(x, y);
    return clone;
}
function getExtremes(boxes) {
    return boxes
        .reduce(([xMin, yMin, xMax, yMax], cv) => [
        Math.min(xMin, cv.getXMin()),
        Math.min(yMin, cv.getYMin()),
        Math.max(xMax, cv.getXMax()),
        Math.max(yMax, cv.getYMax()),
    ], [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY]);
}
class RectangleLocation {
    constructor(width, length, x, y, rotated) {
        this.rotated = rotated;
        this.rectangle = new Rectangle(width, length);
        this.location = new Location(x, y);
    }
    isRotated() {
        return this.rotated;
    }
    setRotated(rotated) {
        this.rotated = rotated;
    }
    setLocation(x, y) {
        this.location = new Location(x, y);
    }
    getWidth() {
        return this.isRotated() ? this.rectangle.getLength() : this.rectangle.getWidth();
    }
    getLength() {
        return this.isRotated() ? this.rectangle.getWidth() : this.rectangle.getLength();
    }
    getRectangleWidth() {
        return this.rectangle.getWidth();
    }
    getRectangleLength() {
        return this.rectangle.getLength();
    }
    canEnclose(target) {
        return canEnclose(this, target);
    }
    canContain(targetWidth, targetLength) {
        return canContainRectangle(this.rectangle.getWidth(), this.rectangle.getLength(), targetWidth, targetLength);
    }
    ;
    getXMax() {
        return this.location.getX() + this.getWidth() - 1;
    }
    getXMin() {
        return this.location.getX();
    }
    getXCenter() {
        return this.location.getX() + (this.getWidth() / 2);
    }
    getYMax() {
        return this.location.getY() + this.getLength() - 1;
    }
    getYMin() {
        return this.location.getY();
    }
    getYCenter() {
        return this.location.getY() + (this.getLength() / 2);
    }
    copyLeft() {
        return copyToLocation(this, this.location.getX() - this.getWidth(), this.location.getY());
    }
    copyRight() {
        return copyToLocation(this, this.location.getX() + this.getWidth(), this.location.getY());
    }
    copyTop() {
        return copyToLocation(this, this.location.getX(), this.location.getY() + this.getLength());
    }
    copyBottom() {
        return copyToLocation(this, this.location.getX(), this.location.getY() - this.getLength());
    }
    clone() {
        return new RectangleLocation(this.getRectangleWidth(), this.getRectangleLength(), this.location.getX(), this.location.getY(), this.isRotated());
    }
    equalsRectangleLocation(recL2) {
        return this.rectangle.equalsRectangle(recL2.rectangle)
            && this.location.equalsLocation(recL2.location)
            && this.isRotated() === recL2.isRotated();
    }
}

var AlignmentType;
(function (AlignmentType) {
    AlignmentType[AlignmentType["X"] = 0] = "X";
    AlignmentType[AlignmentType["Y"] = 1] = "Y";
    AlignmentType[AlignmentType["XY"] = 2] = "XY";
})(AlignmentType || (AlignmentType = {}));
class Alignment {
    constructor(distance, location, type) {
        this.distance = distance;
        this.location = location;
        this.type = type;
    }
}
function getHorizontalAlignmentLocation(rectangleToAlign, alignTarget, targetPadding = 0, includeInnerAlignment = false) {
    const left = rectangleToAlign.getXMin() - targetPadding;
    const rigth = rectangleToAlign.getXMax() + targetPadding;
    const targetLeft = alignTarget.getXMin();
    const targetRight = alignTarget.getXMax();
    const rightDistance = targetLeft - rigth - 1;
    const leftDistance = left - targetRight - 1;
    const innerLeftDistance = targetLeft - rectangleToAlign.getXMin();
    const innerLeftDistanceAbs = Math.abs(innerLeftDistance);
    const innerRightDistance = targetRight - rectangleToAlign.getXMax();
    const innerRightDistanceAbs = Math.abs(innerRightDistance);
    const rightDistanceAbs = Math.abs(rightDistance);
    const leftDistanceAbs = Math.abs(left - targetRight - 1);
    const type = AlignmentType.X;
    // Purpose same location, already aligned
    let alignmentDistance = Math.max(rightDistanceAbs, leftDistanceAbs);
    let location = new Location(left + targetPadding, rectangleToAlign.getYMin());
    // Purpose outer aligment to the target left side
    if (rightDistanceAbs < alignmentDistance) {
        alignmentDistance = rightDistanceAbs;
        location = new Location(rectangleToAlign.getXMin() + rightDistance, rectangleToAlign.getYMin());
    }
    // Purpose outer alignment to the target right side
    if (leftDistanceAbs < alignmentDistance) {
        alignmentDistance = leftDistanceAbs;
        location = new Location(rectangleToAlign.getXMin() - leftDistance, rectangleToAlign.getYMin());
    }
    if (includeInnerAlignment) {
        // Purpose inner aligment to target left side
        if (innerLeftDistanceAbs < alignmentDistance) {
            alignmentDistance = innerLeftDistanceAbs;
            location = new Location(rectangleToAlign.getXMin() + innerLeftDistance, rectangleToAlign.getYMin());
        }
        // Purpose inner aligment to target right side
        if (innerRightDistanceAbs < alignmentDistance) {
            alignmentDistance = innerRightDistanceAbs;
            location = new Location(rectangleToAlign.getXMin() + innerRightDistance, rectangleToAlign.getYMin());
        }
    }
    return new Alignment(alignmentDistance, location, type);
}
function getVerticalAlignmentLocation(rectangleToAlign, alignTarget, targetPadding = 0, includeInnerAlignment = false) {
    const bottom = rectangleToAlign.getYMin() - targetPadding;
    const top = rectangleToAlign.getYMax() + targetPadding;
    const targetBottom = alignTarget.getYMin();
    const targetTop = alignTarget.getYMax();
    const topDistance = targetBottom - top - 1;
    const bottomDistance = bottom - targetTop - 1;
    const innerBottomDistance = targetBottom - rectangleToAlign.getYMin();
    const innerBottomDistanceAbs = Math.abs(innerBottomDistance);
    const innerTopDistance = targetTop - rectangleToAlign.getYMax();
    const innerTopDistanceAbs = Math.abs(innerTopDistance);
    const topDistanceAbs = Math.abs(topDistance);
    const bottomDistanceAbs = Math.abs(bottomDistance);
    const type = AlignmentType.Y;
    // Purpose same location, already aligned
    let alignmentDistance = Math.max(topDistanceAbs, bottomDistanceAbs);
    let location = new Location(rectangleToAlign.getXMin(), rectangleToAlign.getYMin());
    // Purpose outer alignment to the top of target
    if (bottomDistanceAbs < alignmentDistance) {
        alignmentDistance = bottomDistanceAbs;
        location = new Location(rectangleToAlign.getXMin(), rectangleToAlign.getYMin() - bottomDistance);
    }
    // Purpose outer alignment to the bottom of target
    if (topDistanceAbs < alignmentDistance) {
        alignmentDistance = topDistanceAbs;
        location = new Location(rectangleToAlign.getXMin(), rectangleToAlign.getYMin() + topDistance);
    }
    if (includeInnerAlignment) {
        // Purpose alignment to inner bottom of target
        if (innerBottomDistanceAbs < alignmentDistance) {
            alignmentDistance = innerBottomDistanceAbs;
            location = new Location(rectangleToAlign.getXMin(), rectangleToAlign.getYMin() + innerBottomDistance);
        }
        // Purpose alignment to inner top of target
        if (innerTopDistanceAbs < alignmentDistance) {
            alignmentDistance = innerTopDistanceAbs;
            location = new Location(rectangleToAlign.getXMin(), rectangleToAlign.getYMin() + innerTopDistance);
        }
    }
    return new Alignment(alignmentDistance, location, type);
}
/**
 * Helper function to create alignment combinations,
 * merging persumed X alignments with persumed Y alignments WITHOUT TYPE CHECK,
 * and calculated new alignment distance and location
 * @param alignmentListX
 * @param alignmentListY
 */
function getXYAligmentCombinations(alignmentListX, alignmentListY) {
    const alignmentsToFlatten = alignmentListX.map(xAlignment => {
        return alignmentListY.map(yAlignment => {
            return new Alignment(Math.sqrt(Math.pow(xAlignment.distance, 2) + Math.pow(yAlignment.distance, 2)), new Location(xAlignment.location.getX(), yAlignment.location.getY()), AlignmentType.XY);
        });
    });
    // Flatten one level
    return alignmentsToFlatten.reduce((prevValue, currentValue) => prevValue.concat(currentValue), []);
}
function getBestAlignmentLocationFor(toAlign, rectangles, alignmentProximity, padding = 0, includeInnerAlignment = true, allowedOverlaps = [], customFilter) {
    const toAlignOuter = grow(toAlign, alignmentProximity + 1);
    const boxCandidates = getOverlappingRectangleLocations([toAlignOuter], rectangles, padding);
    if (boxCandidates.length < 1) {
        return;
    }
    const horizontalAlignmentAlternatives = boxCandidates.map(bc => getHorizontalAlignmentLocation(toAlign, bc[1], padding, includeInnerAlignment));
    const verticalAligmentAlternatives = boxCandidates.map(bc => getVerticalAlignmentLocation(toAlign, bc[1], padding, includeInnerAlignment));
    const combinationalAligmentAlternatives = getXYAligmentCombinations(horizontalAlignmentAlternatives, verticalAligmentAlternatives);
    const alignmentAlternatives = horizontalAlignmentAlternatives
        .concat(verticalAligmentAlternatives)
        .concat(combinationalAligmentAlternatives)
        .sort((alignmentAlternative1, alignmentAlternative2) => {
        // XY alignments are sorted as better than X and Y alignments.
        if (alignmentAlternative1.type !== alignmentAlternative2.type) {
            if (alignmentAlternative1.type === 2) {
                return -1;
            }
            if (alignmentAlternative2.type === 2) {
                return +1;
            }
        }
        return alignmentAlternative1.distance - alignmentAlternative2.distance;
    });
    let filteredAlternatives = alignmentAlternatives.filter(result => {
        const alternative = copyToLocation(toAlign, result.location.getX(), result.location.getY());
        return result.distance <= alignmentProximity && !hasOverlapInLists([alternative], rectangles.filter(r => !allowedOverlaps.some(ao => ao === r)), padding);
    });
    if (customFilter) {
        filteredAlternatives = filteredAlternatives.filter(result => customFilter(copyToLocation(toAlign, result.location.getX(), result.location.getY())));
    }
    if (filteredAlternatives.length > 0) {
        return filteredAlternatives[0].location;
    }
}
function hasOverlapInLists(recLs1, recLs2, paddingRec1 = 0, paddingRec2 = 0) {
    return recLs1.find(r1 => recLs2.find(r2 => rectangleLocationOverlap(r1, r2, paddingRec1, paddingRec2))) !== undefined;
}
function getOverlappingRectangleLocations(recLs1, recLs2, paddingRec1 = 0, paddingRec2 = 0) {
    const overlappingRectangleLocations = new Array();
    recLs1.forEach(r1 => {
        recLs2.forEach(r2 => {
            if (rectangleLocationOverlap(r1, r2, paddingRec1, paddingRec2)) {
                overlappingRectangleLocations.push([r1, r2]);
            }
        });
    });
    return overlappingRectangleLocations;
}
function getInnerTangentialLocationFromOuterRectangleLocation(frame, outerRectangleLocation) {
    const outerX = outerRectangleLocation.getXMin();
    const outerY = outerRectangleLocation.getYMin();
    const fMinX = frame.getXMin();
    const fMinY = frame.getYMin();
    const fMaxX = frame.getXMax();
    const fMaxY = frame.getYMax();
    const rWidth = outerRectangleLocation.getWidth();
    const rLength = outerRectangleLocation.getLength();
    const x = outerX < fMinX ? fMinX :
        outerX >= (fMaxX - rWidth) ? fMaxX - rWidth + 1 : outerX;
    const y = outerY < fMinY ? fMinY :
        outerY >= (fMaxY - rLength) ? fMaxY - rLength + 1 : outerY;
    return new Location(x, y);
}
/**
 * This method checks if two recLes overlap
 *
 * @param recL1 - RectangleLocation for overlap calculation
 * @param recL2 - RectangleLocation for overlap calculation
 * @returns boolean indicating if the recLes overlap or not
 */
function rectangleLocationOverlap(recL1, recL2, paddingRec1 = 0, paddingRec2 = 0) {
    return recLocationOverlapX(recL1, recL2, paddingRec1, paddingRec2) && recLocationOverlapY(recL1, recL2, paddingRec1, paddingRec2);
}
function lineOverlap(aMin, aMax, bMin, bMax) {
    return aMax >= bMin && bMax >= aMin;
}
function pointLineOverlap(lMin, lMax, p) {
    return p >= lMin && p <= lMax;
}
function locationOverlapRecLocation(location, rec) {
    return pointLineOverlap(rec.getXMin(), rec.getXMax(), location.getX()) && pointLineOverlap(rec.getYMin(), rec.getYMax(), location.getY());
}
function recLocationOverlapX(recL1, recL2, paddingRec1 = 0, paddingRec2 = 0) {
    return lineOverlap(recL1.getXMin() - paddingRec1, recL1.getXMax() + paddingRec1, recL2.getXMin() - paddingRec2, recL2.getXMax() + paddingRec2);
}
function recLocationOverlapY(recL1, recL2, paddingRec1 = 0, paddingRec2 = 0) {
    return lineOverlap(recL1.getYMin() - paddingRec1, recL1.getYMax() + paddingRec1, recL2.getYMin() - paddingRec2, recL2.getYMax() + paddingRec2);
}

var AxisDirection;
(function (AxisDirection) {
    AxisDirection[AxisDirection["Y_NEGATIVE"] = 0] = "Y_NEGATIVE";
    AxisDirection[AxisDirection["X_POSITIVE"] = 1] = "X_POSITIVE";
    AxisDirection[AxisDirection["Y_POSITIVE"] = 2] = "Y_POSITIVE";
    AxisDirection[AxisDirection["X_NEGATIVE"] = 3] = "X_NEGATIVE";
})(AxisDirection || (AxisDirection = {}));
function isHorizontal(direction) {
    switch (direction) {
        case AxisDirection.Y_POSITIVE:
        case AxisDirection.Y_NEGATIVE:
            return false;
        case AxisDirection.X_NEGATIVE:
        case AxisDirection.X_POSITIVE:
            return true;
    }
}

function rotateDirection(direction, counterClockwise = false) {
    switch (direction) {
        case AxisDirection.Y_POSITIVE:
            return counterClockwise ? AxisDirection.X_NEGATIVE : AxisDirection.X_POSITIVE;
        case AxisDirection.X_NEGATIVE:
            return counterClockwise ? AxisDirection.Y_NEGATIVE : AxisDirection.Y_POSITIVE;
        case AxisDirection.Y_NEGATIVE:
            return counterClockwise ? AxisDirection.X_POSITIVE : AxisDirection.X_NEGATIVE;
        case AxisDirection.X_POSITIVE:
            return counterClockwise ? AxisDirection.Y_POSITIVE : AxisDirection.Y_NEGATIVE;
    }
}
function roundTohalf(value) {
    return Math.round(value * 2) / 2;
}
function rotatePointCCW(r, x, y) {
    return [roundTohalf(x * Math.cos(r) - y * Math.sin(r)), roundTohalf(x * Math.sin(r) + y * Math.cos(r))];
}
function vectorSub(vx, vy, vsx, vsy) {
    return [vx - vsx, vy - vsy];
}
function vectorAdd(vx, vy, vax, vay) {
    return [vx + vax, vy + vay];
}
function rotateDirectedRectangleLocation90Degrees(drl, counterClockwise = false, rotationCenterX, rotationCenterY) {
    const [xMin, yMin, xMax, yMax] = [drl.getXMin(), drl.getYMin(), drl.getXMax(), drl.getYMax()];
    // We want to rotate around box center, so we need to translate coordinates to box center
    // Translate to center relative coordinates
    const [xMinTR, yMinTR, xMaxTR, yMaxTR] = [...vectorSub(xMin, yMin, rotationCenterX, rotationCenterY), ...vectorSub(xMax, yMax, rotationCenterX, rotationCenterY)];
    // Rotate counterclockwise or counter clockwise
    if (counterClockwise) {
        const [xMaxR, yMinR, xMinR, yMaxR] = [...rotatePointCCW(Math.PI * 1 / 2, xMinTR, yMinTR), ...rotatePointCCW(Math.PI * 1 / 2, xMaxTR, yMaxTR)];
        // Calculate world coordinates after rotation
        drl.setLocation(...vectorAdd(rotationCenterX, rotationCenterY, xMinR, yMinR));
    }
    // Rotate clockwise
    else {
        const [xMinR, yMaxR, xMaxR, yMinR] = [...rotatePointCCW(Math.PI * 3 / 2, xMinTR, yMinTR), ...rotatePointCCW(Math.PI * 3 / 2, xMaxTR, yMaxTR)];
        // Calculate world coordinates after rotation
        drl.setLocation(...vectorAdd(rotationCenterX, rotationCenterY, xMinR, yMinR));
    }
    drl.setDirection(rotateDirection(drl.getDirection(), counterClockwise));
}
function rotateDirectedRectangleLocations90Degrees(drls, counterClockwise = false) {
    const [xMin, yMin, xMax, yMax] = getExtremes(drls);
    const [rotationCenterX, rotationCenterY] = [xMin + (xMax - xMin) / 2, yMin + (yMax - yMin) / 2];
    drls.map(drl => rotateDirectedRectangleLocation90Degrees(drl, counterClockwise, rotationCenterX, rotationCenterY));
    return drls;
}
class DirectedRectangleLocation extends RectangleLocation {
    constructor(width, length, x, y, direction) {
        super(width, length, x, y, isHorizontal(direction));
        this.direction = direction;
    }
    toCartesian() {
        const [x1, y1] = [this.getXMin(), this.getYMin()];
        return [[x1, y1], [x1 + this.getWidth(), y1 + this.getLength()]];
    }
    getDirection() {
        return this.direction;
    }
    setDirection(direction) {
        super.setRotated(isHorizontal(direction));
        this.direction = direction;
    }
    rotate(counterClockwise = false) {
        const [xMin, yMin, xMax, yMax] = [this.getXMin(), this.getYMin(), this.getXMax(), this.getYMax()];
        const [bcx, bcy] = [xMin + (xMax - xMin) / 2, yMin + (yMax - yMin) / 2];
        rotateDirectedRectangleLocation90Degrees(this, counterClockwise, bcx, bcy);
    }
    clone() {
        const drl = new DirectedRectangleLocation(this.getRectangleWidth(), this.getRectangleLength(), this.location.getX(), this.location.getY(), this.direction);
        return drl;
    }
    equalsDirectedRectangleLocation(drl2) {
        return super.equalsRectangleLocation(drl2)
            && this.getDirection() === drl2.getDirection();
    }
}

/**
 * Box class represents the boxes in the box-packing component. return this
 * It will describe boxes in input packing and output packing(edit events)
 * @param customSVG is an optional SVG string,
 * providing the opportunity to have custom SVG drawn on top of the box within the box-packing component
 */
class Box extends DirectedRectangleLocation {
    constructor(id, width, length, x, y, direction, customSVG) {
        super(width, length, x, y, direction);
        this.id = id;
        this.customSVG = customSVG;
    }
    getId() {
        return this.id;
    }
    getCustomSVG() {
        return this.customSVG;
    }
    clone() {
        return new Box(this.getId(), this.getRectangleWidth(), this.getRectangleLength(), this.location.getX(), this.location.getY(), this.getDirection(), this.getCustomSVG());
    }
}

export { AxisDirection as A, Box as B, Location as L, RectangleLocation as R, getOverlappingRectangleLocations as a, getInnerTangentialLocationFromOuterRectangleLocation as b, copyToLocation as c, canEnclose as d, getBestAlignmentLocationFor as e, rectangleLocationOverlap as f, getExtremes as g, locationOverlapRecLocation as l, rotateDirectedRectangleLocations90Degrees as r };
